import { useCallback, useRef, useState } from 'react';
import { FileRejection } from 'react-dropzone';

import { useFileDropperContext } from './file-dropper.context';
import { useFileUploadProgress } from './file-upload';
import {
  AcceptTypes,
  FILE_TYPE_ERROR_CODE,
  UPLOAD_ERROR,
} from './file-dropper.constants';
import { getAcceptError } from './file-dropper.utils';

type Props = {
  name: string;
  onChange: (key: string, fileName: string, file: File) => void;
  accept?: AcceptTypes;
  skipS3Upload?: boolean;
};

export const useFileDropperHandlers = ({
  name,
  onChange,
  accept,
  skipS3Upload,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { setFile, setError } = useFileDropperContext(name);

  const { upload, progress: s3Progress } = useFileUploadProgress(name);

  // Track completed progress for skipS3Upload mode
  const [skipUploadProgress, setSkipUploadProgress] = useState<
    ProgressEvent | undefined
  >(undefined);

  const handleUploadFile = useCallback(
    (file: File) => {
      if (skipS3Upload) {
        // Set file in context to show uploaded state
        setFile(file);
        // Create a completed progress event to show as uploaded
        const completedProgress = {
          loaded: file.size,
          total: file.size,
        } as ProgressEvent;
        setSkipUploadProgress(completedProgress);
        // Call onChange with empty key since we're not uploading to S3
        onChange('', file.name, file);
        return;
      }
      setFile(file);
      upload(file)
        .then((s3Key: string) => {
          onChange(s3Key, file.name, file);
        })
        .catch(() => {});
    },
    [setFile, upload, onChange, skipS3Upload]
  );

  const onDrop = useCallback(
    (files: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length) {
        const errorCode = fileRejections[0].errors[0].code;
        setError(
          errorCode === FILE_TYPE_ERROR_CODE && accept
            ? getAcceptError(accept)
            : UPLOAD_ERROR
        );
        return;
      }

      if (files.length) {
        handleUploadFile(files[0]);
      }
    },
    [accept, handleUploadFile, setError]
  );

  const launchFileDialog = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef]);

  const inputId = `inputFile_${name}`;

  // Use skipUploadProgress when skipS3Upload is true, otherwise use s3Progress
  const progress = skipS3Upload ? skipUploadProgress : s3Progress;

  return {
    onDrop,
    launchFileDialog,
    inputRef,
    inputId,
    progress,
  };
};
